import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "PlasticRecyclingFiltration",
    relatedTopic: "polymer",
    hero: {
      categoryBadge: "Polymer Filtration Applications",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Plastic Recycling Melt Filtration",
      lead: "Engineered stainless steel woven wire mesh packs, sintered filter discs, and continuous changer screen belts designed for high contaminant loads in post-consumer and industrial polymer reprocessing.",
      secondaryCta: {
        label: "Polymer Filtration Overview",
        to: "/PolymerFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
        alt: "Stainless steel multi-layer screen pack and filter disc assembly for plastic recycling melt filtration",
      },
      cardNote: "Multi-layer packs, spot-welded sets & belt coils",
      cardLink: {
        label: "View Screen Packs",
        to: "/FilterComponentsPage",
      },
      specs: [
        { label: "Retention Rating", value: "10–200 µm" },
        { label: "Alloy Grades", value: "AISI 304 / 316L" },
        { label: "Construction", value: "Spot Welded / Rimmed / Belt" },
        { label: "Application", value: "PCR / PIR Extrusion & Pelleting" },
      ],
    },
    overview: {
      eyebrow: "Process Requirements",
      heading: "Continuous Melt Cleaning Under Severe Contaminant Loading",
      paragraphs: [
        "Reprocessing post-consumer and post-industrial plastic polymers presents intense filtration challenges. High levels of solid particulate, un-melted gels, paper fibers, aluminum flakes, and degraded carbon specks must be continuously captured from the viscous melt stream before it reaches pelletizing dies or profile tooling.",
        "To prevent frequent line stoppages, modern recycling extrusion systems rely on hydraulic slide-plate changers, continuous rotary discs, or auto-screen belt coils. The wire mesh media inside these systems must combine high dirt-holding capacity, precise particle retention, and structural strength to withstand extreme differential pressures without tearing or wire migration.",
        "MutualFilter supplies multi-layer screen packs with coarse square mesh support grids, precision Dutch weave control layers, and sintered laminate discs engineered to deliver predictable backpressure buildup and extended online service life.",
      ],
      keyPoints: [
        "High-tensile square wire support grids protect fine control layers from severe extrusion differential pressures and prevent screen blowout.",
        "Calibrated plain Dutch and reverse Dutch weaves capture degraded gels, carbon specks, and fine metallic residues to stabilize head pressure.",
        "Available as multi-layer spot-welded packs, rimmed aluminum-framed discs, and continuous auto-screen belt coils for automated screen changers.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/polymer_extrusion_filter.png",
        alt: "Industrial continuous melt filtration screen and extrusion breaker plate assembly",
      },
    },
    specTable: {
      heading: "Standard Mesh Media Configurations for Plastic Recycling",
      intro: "Representative screen pack configurations and woven wire mesh layers utilized in polymer recycling extruders and screen changers.",
      columns: [
        { key: "configuration", label: "Media Configuration", align: "left" },
        { key: "micronRating", label: "Nominal Retention", align: "right" },
        { key: "layerStructure", label: "Typical Layer Structure", align: "left" },
        { key: "changerType", label: "Changer Compatibility", align: "left" },
      ],
      rows: [
        {
          id: "coarse-prep",
          configuration: "Heavy Wash / Coarse Straining",
          micronRating: "150–200 µm",
          layerStructure: "2-layer or 3-layer square weave (20 / 80 / 20 mesh)",
          changerType: "Manual / hydraulic slide-plate changers",
        },
        {
          id: "standard-pellet",
          configuration: "General Pelleting & Reprocessing",
          micronRating: "60–120 µm",
          layerStructure: "3-layer to 5-layer spot welded (20 / 60 / 150 / 60 / 20)",
          changerType: "Dual-piston & rotary continuous changers",
        },
        {
          id: "fine-purification",
          configuration: "Fine Sheet / Film Grade PCR",
          micronRating: "25–50 µm",
          layerStructure: "Multi-layer plain Dutch with protective backing grids",
          changerType: "Continuous backflush & auto-screen changers",
        },
        {
          id: "micro-filtration",
          configuration: "High-Purity Technical Regrind",
          micronRating: "10–25 µm",
          layerStructure: "Sintered multi-layer laminate or twill Dutch assembly",
          changerType: "High-pressure continuous melt filter systems",
        },
      ],
      caption: "All filter packs and screen coils are fabricated to order in AISI 304 or AISI 316L stainless steel according to breaker plate dimensions.",
    },
    features: {
      heading: "Engineering Advantages for Recycling Operations",
      intro: "Key design characteristics supporting high uptime and consistent melt quality across polymer recycling lines.",
      items: [
        {
          title: "Blowout-Resistant Structural Support",
          text: "Heavy-gauge square weave backing meshes prevent media deflection into breaker plate drill holes during rapid pressure surges.",
        },
        {
          title: "Calibrated Pore Geometry",
          text: "Accurately woven Dutch weaves provide definite particle cutoffs, preventing elongated gels and elastomer particles from squeezing through.",
        },
        {
          title: "Sealed Outer Rim Options",
          text: "Aluminum or copper rimmed borders eliminate edge fraying and prevent unfiltered polymer bypass around the circumference of breaker plates.",
        },
        {
          title: "Continuous Coil Production",
          text: "Reverse Dutch weave filter belts with reinforced selvage edges deliver reliable indexing in automatic pressure-controlled belt changers.",
        },
      ],
    },
    related: {
      heading: "Related Applications & Product Families",
      intro: "Explore complementary polymer filtration solutions and standard stainless steel wire mesh product lines.",
      cards: [
        {
          title: "Polymer Melt Filtration",
          text: "Continuous melt filtration solutions for virgin resin extrusion, synthetic fiber spinning, cast film, and high-purity compounding.",
          to: "/PolymerMeltFiltrationApplicationsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-15b8ef.png",
            alt: "Continuous hydraulic polymer melt screen changer assembly and filter pack",
          },
          badge: "Sibling Application",
          linkLabel: "View Application",
        },
        {
          title: "Polymer Filtration Hub",
          text: "Comprehensive overview of stainless steel filter media, screen packs, and continuous changer elements for polymer processing.",
          to: "/PolymerFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-polymer-ext-65ec06.png",
            alt: "Polymer melt extrusion filter packs and engineering overview",
          },
          badge: "Parent Application",
          linkLabel: "View Overview",
        },
        {
          title: "Filter Components",
          text: "Custom stamped filter discs, multi-layer spot welded screen packs, rimmed assemblies, and extruder screen coils in 304 and 316L.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "Stainless steel filter discs and screen packs for industrial equipment",
          },
          badge: "Product Family",
          linkLabel: "Explore Components",
        },
        {
          title: "Woven Wire Mesh",
          text: "Square weave and Dutch weave industrial wire cloth manufactured to ISO 9044 and ASTM E2016 for precise separation and support.",
          to: "/WovenWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Plain Dutch weave stainless steel wire cloth for melt filtration",
          },
          badge: "Product Family",
          linkLabel: "Explore Woven Mesh",
        },
        {
          title: "Sintered Wire Mesh",
          text: "Diffusion-bonded multilayer stainless steel laminates offering rigid structure, zero wire shedding, and repeated ultrasonic cleanability.",
          to: "/SinteredWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Multilayer sintered wire mesh laminate disc for melt filtration",
          },
          badge: "Product Family",
          linkLabel: "Explore Sintered Mesh",
        },
        {
          title: "Wedge Wire Screen",
          text: "Continuous slot all-welded wedge wire screens and cylindrical baskets for high-capacity dewatering and initial solids screening.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/industrial-wedge-wire-screen-panel-showi-bb2a47.png",
            alt: "Wedge wire screen panel for high-load industrial separation",
          },
          badge: "Product Family",
          linkLabel: "Explore Wedge Wire",
        },
      ],
    },
    cta: {
      id: "plastic-recycling-cta",
      eyebrow: "Recycling Filtration Consultation",
      heading: "Request Custom Screen Packs for Your Recycling Line",
      lead: "Provide your breaker plate diameter, screen changer model, polymer type, and target micron rating to receive engineered screen pack specifications and pricing.",
      secondaryLabel: "Contact Technical Sales",
      secondaryTo: "/ContactPage",
      note: "Standard and custom diameters available in AISI 304 and AISI 316L with prompt quotation support.",
    },
  },
  zh: {
    breadcrumbKey: "PlasticRecyclingFiltration",
    relatedTopic: "polymer",
    hero: {
      categoryBadge: "聚合物过滤工业应用",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "塑料回收连续熔体过滤",
      lead: "工程级不锈钢编织网包、烧结滤盘及连续换网走网滤带，专为消费后与工业再生塑料高杂质载荷熔体造粒与挤出工况设计。",
      secondaryCta: {
        label: "返回聚合物过滤总览",
        to: "/PolymerFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
        alt: "塑料回收熔体过滤用不锈钢多层组合滤网片与滤盘总成",
      },
      cardNote: "多层点焊网包、铝包边圆盘与走网滤带",
      cardLink: {
        label: "查看挤出组合滤网",
        to: "/FilterComponentsPage",
      },
      specs: [
        { label: "截留精度", value: "10–200 µm" },
        { label: "合金体系", value: "AISI 304 / 316L" },
        { label: "交付结构", value: "点焊网包 / 包边盘 / 连续带" },
        { label: "典型工况", value: "PCR / PIR 再生挤出造粒" },
      ],
    },
    overview: {
      eyebrow: "工艺运行需求",
      heading: "高固含杂质工况下的连续熔体净化",
      paragraphs: [
        "消费后再生料（PCR）与工业回收料（PIR）在高温熔融挤出过程中携带大量固相杂质。未熔胶块、纸屑纤维、铝箔碎屑以及受热裂解形成的碳化黑点，必须在进入造粒切粒模头或型材挤出模具前被连续拦截清除。",
        "为避免频繁停机清洗换网，现代塑料再生挤出造粒线广泛采用液压滑板换网器、双柱式连续换网器或全自动走网换网器。内部搭载的金属丝网介质必须同时具备高容污能力、明确的微米级截留界限以及优异的抗压差抗冲击机械刚性，杜绝爆网与金属丝位移。",
        "互拓丝网供应由粗目方孔支撑网、精密荷兰编织过滤层及烧结复合网板组合而成的多层换网总成，在严苛挤出背压下提供稳定平缓的压差上升曲线与更长在线使用寿命。",
      ],
      keyPoints: [
        "高抗拉方孔不锈钢承托网有效缓冲高压挤出脉冲压力，防止精滤层破损穿孔与金属断丝渗透。",
        "校准级平纹与反向荷兰编织网精准截留热降解碳化黑点、未熔凝胶及微细金属杂质，维持稳定机头熔压。",
        "提供多层点焊网包、铝合金包边圆盘滤片及自动走网连续过滤带，满足全自动连续换网生产需求。",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/polymer_extrusion_filter.png",
        alt: "工业连续熔体换网器专用多层不锈钢过滤网包与分流板组件",
      },
    },
    specTable: {
      heading: "塑料回收常用金属丝网配置",
      intro: "塑料再生挤出与熔体换网系统中常用的标准网包组合层数与过滤介质匹配参考。",
      columns: [
        { key: "configuration", label: "应用过滤配置", align: "left" },
        { key: "micronRating", label: "标称截留精度", align: "right" },
        { key: "layerStructure", label: "典型多层组合结构", align: "left" },
        { key: "changerType", label: "适配换网器类型", align: "left" },
      ],
      rows: [
        {
          id: "coarse-prep",
          configuration: "重度清洗粗滤 / 粗级杂质拦截",
          micronRating: "150–200 µm",
          layerStructure: "2层或3层方孔编织网组合（20 / 80 / 20 目）",
          changerType: "手动换网器 / 液压双工位滑板换网器",
        },
        {
          id: "standard-pellet",
          configuration: "常规再生造粒通用过滤",
          micronRating: "60–120 µm",
          layerStructure: "3层至5层点焊组合（20 / 60 / 150 / 60 / 20 目）",
          changerType: "双柱连续换网器与旋转盘式换网器",
        },
        {
          id: "fine-purification",
          configuration: "高品质板材与薄膜级再生",
          micronRating: "25–50 µm",
          layerStructure: "多层平纹荷兰编织层配合高强度方孔托网",
          changerType: "连续反冲洗换网器与自动走网机",
        },
        {
          id: "micro-filtration",
          configuration: "工程塑料与改性高纯造粒",
          micronRating: "10–25 µm",
          layerStructure: "5层烧结不锈钢网板或斜纹荷兰高密组合",
          changerType: "高压连续熔体精密过滤系统",
        },
      ],
      caption: "所有滤网组合均按换网器分流板（Breaker Plate）实际装配尺寸定制加工，支持 AISI 304 及 316L 材质。",
    },
    features: {
      heading: "塑料再生回收工况工程优势",
      intro: "确保塑料回收造粒线高运转率与熔体均一纯净的核心结构特性。",
      items: [
        {
          title: "抗击穿刚性承托结构",
          text: "重型粗线方孔托网防止精细滤网在瞬间背压脉冲下凹陷挤入分流板多孔孔道。",
        },
        {
          title: "校准级精密几何微孔",
          text: "精密编织荷兰网孔道曲折稳定，杜绝受热变形的未熔胶粒在熔体推力下发生拉伸穿透。",
        },
        {
          title: "包边密封防旁漏工艺",
          text: "铝制或紫铜包边紧密贴合模槽，防止熔融物料从滤盘外缘缝隙旁通泄漏未滤杂质。",
        },
        {
          title: "连续自动走网带卷",
          text: "反向荷兰编织连续滤带具备紧密牢固的包边锁口，在自动压差步进走网机中平稳移行无跑偏。",
        },
      ],
    },
    related: {
      heading: "相关应用方案与核心产品体系",
      intro: "了解更多聚合物过滤细分应用及相配套的不锈钢丝网产品大类。",
      cards: [
        {
          title: "聚合物熔体过滤",
          text: "原生树脂连续挤出、化纤纺丝组件、流延薄膜及高纯色母料熔体净化方案。",
          to: "/PolymerMeltFiltrationApplicationsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-15b8ef.png",
            alt: "连续液压聚合物熔体换网器组件与滤网总成",
          },
          badge: "同级应用",
          linkLabel: "查看应用方案",
        },
        {
          title: "聚合物过滤总览",
          text: "聚合物熔体挤出、化纤纺丝与塑料回收行业全套不锈钢滤材综合工程方案。",
          to: "/PolymerFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-polymer-ext-65ec06.png",
            alt: "聚合物熔体挤出滤网包与工程方案总览",
          },
          badge: "上级应用",
          linkLabel: "查看总览",
        },
        {
          title: "过滤元件组件",
          text: "多层点焊网包、铝包边圆盘滤片、腰形滤网及连续换网走网滤带定制加工。",
          to: "/FilterComponentsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "工业设备用不锈钢滤片与换网网包",
          },
          badge: "产品大类",
          linkLabel: "探索过滤元件",
        },
        {
          title: "不锈钢编织丝网",
          text: "方孔丝网与荷兰编织滤布，严格按照 ISO 9044 与 ASTM E2016 规范织造。",
          to: "/WovenWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "熔体过滤用平纹荷兰编织不锈钢滤布",
          },
          badge: "产品大类",
          linkLabel: "探索编织丝网",
        },
        {
          title: "烧结复合丝网",
          text: "多层真空扩散焊接不锈钢烧结板与滤圆盘，刚性优越、无断丝脱落且耐反复清洗。",
          to: "/SinteredWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "熔体过滤用多层烧结不锈钢网板与滤片",
          },
          badge: "产品大类",
          linkLabel: "探索烧结丝网",
        },
        {
          title: "约翰逊梯形丝网",
          text: "全焊式楔形丝滤板与圆柱形滤芯，用于高固含量废塑料水洗脱水与前端初级分离。",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/industrial-wedge-wire-screen-panel-showi-bb2a47.png",
            alt: "用于高负载工业分离的楔形丝筛网板",
          },
          badge: "产品大类",
          linkLabel: "探索楔形丝网",
        },
      ],
    },
    cta: {
      id: "plastic-recycling-cta",
      eyebrow: "回收过滤技术方案咨询",
      heading: "为您的塑料造粒线定制换网过滤组合方案",
      lead: "提供分流板直径、换网器型号、加工树脂类型及目标过滤微米精度，获取定制网包工程设计与报价。",
      secondaryLabel: "联系技术销售团队",
      secondaryTo: "/ContactPage",
      note: "支持 AISI 304 与 316L 标准及异形规格定制，提供专业及时的技术响应。",
    },
  },
  es: {
    breadcrumbKey: "PlasticRecyclingFiltration",
    relatedTopic: "polymer",
    hero: {
      categoryBadge: "Aplicaciones de filtración de polímeros",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Filtración de fundido para reciclaje de plástico",
      lead: "Paquetes de malla de alambre de acero inoxidable, discos sinterizados y bandas continuas diseñados para altas cargas de contaminantes en el reprocesamiento de polímeros.",
      secondaryCta: {
        label: "Visión general de polímeros",
        to: "/PolymerFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
        alt: "Conjunto de paquete de malla y disco filtrante para reciclaje de plástico",
      },
      cardNote: "Packs soldados por puntos, discos ribeteados y bobinas continuas",
      cardLink: {
        label: "Ver paquetes de mallas",
        to: "/FilterComponentsPage",
      },
      specs: [
        { label: "Grado de retención", value: "10–200 µm" },
        { label: "Aleaciones", value: "AISI 304 / 316L" },
        { label: "Formatos", value: "Packs / Discos / Bobinas" },
        { label: "Aplicación", value: "Extrusión y peletizado PCR / PIR" },
      ],
    },
    overview: {
      eyebrow: "Requisitos de proceso",
      heading: "Limpieza continua de masa fundida con alta carga de impurezas",
      paragraphs: [
        "El reprocesamiento de plásticos reciclados postconsumo e industriales presenta retos severos. Altas concentraciones de partículas sólidas, geles no fundidos, restos de papel, virutas metálicas y motas carbonizadas deben ser retenidas continuamente de la corriente fundida antes del peletizado.",
        "Para evitar paradas frecuentes, los sistemas de extrusión de reciclaje emplean cambiadores de placa deslizante, discos rotativos o cambiadores automáticos de banda continua. Los medios filtrantes metálicos deben combinar alta capacidad de retención, corte de partículas preciso y resistencia mecánica ante presiones diferenciales elevadas.",
        "MutualFilter suministra paquetes multicapa con mallas de soporte cuadradas reforzadas, capas de control en tejido Dutch y discos sinterizados diseñados para un aumento de presión predecible y una mayor vida útil.",
      ],
      keyPoints: [
        "Mallas de soporte cuadradas de alta resistencia que protegen las capas finas contra picos de presión y evitan deformaciones o roturas.",
        "Tejidos Dutch lisos e inversos calibrados que capturan geles degradados, motas negras y finos metálicos para estabilizar la presión del cabezal.",
        "Disponibles como paquetes multicapa soldados por puntos, discos ribeteados con marco de aluminio y bobinas continuas para cambiadores automáticos.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/polymer_extrusion_filter.png",
        alt: "Malla de filtración de fundido continuo y placa rompedora de extrusión",
      },
    },
    specTable: {
      heading: "Configuraciones estándar de malla para reciclaje de plástico",
      intro: "Combinaciones típicas de paquetes de mallas y capas de alambre tejido para extrusoras de reciclaje de polímeros.",
      columns: [
        { key: "configuration", label: "Configuración", align: "left" },
        { key: "micronRating", label: "Retención nominal", align: "right" },
        { key: "layerStructure", label: "Estructura típica de capas", align: "left" },
        { key: "changerType", label: "Compatibilidad de cambiador", align: "left" },
      ],
      rows: [
        {
          id: "coarse-prep",
          configuration: "Lavado pesado / Desbaste grueso",
          micronRating: "150–200 µm",
          layerStructure: "2 o 3 capas de malla cuadrada (20 / 80 / 20 mesh)",
          changerType: "Cambiadores de placa deslizante manual o hidráulico",
        },
        {
          id: "standard-pellet",
          configuration: "Peletizado y reprocesamiento general",
          micronRating: "60–120 µm",
          layerStructure: "3 a 5 capas soldadas por puntos (20 / 60 / 150 / 60 / 20)",
          changerType: "Cambiadores de doble pistón y continuos rotativos",
        },
        {
          id: "fine-purification",
          configuration: "Lámina y película fina PCR",
          micronRating: "25–50 µm",
          layerStructure: "Multicapa tejido Dutch liso con soporte reforzado",
          changerType: "Cambiadores de retrolavado y bandas continuas",
        },
        {
          id: "micro-filtration",
          configuration: "Plásticos técnicos de alta pureza",
          micronRating: "10–25 µm",
          layerStructure: "Laminado sinterizado multicapa o tejido Dutch cruzado",
          changerType: "Sistemas de filtración continua para alta presión",
        },
      ],
      caption: "Todos los paquetes y bobinas se fabrican bajo pedido en AISI 304 o AISI 316L según las dimensiones de su placa rompedora.",
    },
    features: {
      heading: "Ventajas de ingeniería para operaciones de reciclaje",
      intro: "Características estructurales clave que garantizan máxima operatividad y calidad homogénea del polímero fundido.",
      items: [
        {
          title: "Soporte estructural antiroturas",
          text: "Mallas de soporte cuadradas de grueso calibre que evitan la deformación hacia las perforaciones de la placa rompedora.",
        },
        {
          title: "Geometría de poro calibrada",
          text: "Tejidos Dutch de precisión que aseguran un corte de partículas exacto e impiden el paso de geles estirados.",
        },
        {
          title: "Bordes ribeteados estancos",
          text: "Marcos en aluminio o cobre que sellan los bordes y evitan fugas periféricas de material sin filtrar.",
        },
        {
          title: "Bobinas para avance continuo",
          text: "Telas en tejido Dutch inverso con orillos reforzados para un guiado impecable en cambiadores automáticos.",
        },
      ],
    },
    related: {
      heading: "Aplicaciones relacionadas y gamas de producto",
      intro: "Explore soluciones complementarias en filtración de polímeros y nuestras gamas de malla metálica de acero inoxidable.",
      cards: [
        {
          title: "Filtración de polímeros fundidos",
          text: "Soluciones de filtración continua para resina virgen, hilatura de fibras, láminas y compuestos de alta pureza.",
          to: "/PolymerMeltFiltrationApplicationsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-15b8ef.png",
            alt: "Conjunto de cambiador de filtros continuo para polímero fundido",
          },
          badge: "Aplicación hermana",
          linkLabel: "Ver aplicación",
        },
        {
          title: "Visión general de filtración de polímeros",
          text: "Gama completa de medios filtrantes, paquetes de malla y cambiadores continuos para el procesado de polímeros.",
          to: "/PolymerFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-polymer-ext-65ec06.png",
            alt: "Packs filtrantes para extrusión de polímeros",
          },
          badge: "Aplicación padre",
          linkLabel: "Ver visión general",
        },
        {
          title: "Componentes filtrantes",
          text: "Discos estampados a medida, paquetes soldados por puntos, ensamblajes ribeteados y bobinas de cambiador en 304 y 316L.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "Discos filtrantes de acero inoxidable para extrusión",
          },
          badge: "Familia de producto",
          linkLabel: "Ver componentes",
        },
        {
          title: "Malla de alambre tejido",
          text: "Telas metálicas de malla cuadrada y tejido Dutch según ISO 9044 y ASTM E2016 para retención y soporte.",
          to: "/WovenWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Malla de alambre tejido Dutch liso para filtración de fundido",
          },
          badge: "Familia de producto",
          linkLabel: "Ver malla tejida",
        },
        {
          title: "Malla sinterizada",
          text: "Laminados multicapa sinterizados por difusión que ofrecen rigidez absoluta, cero desprendimiento y limpieza repetida.",
          to: "/SinteredWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Disco laminado de malla sinterizada para filtración",
          },
          badge: "Familia de producto",
          linkLabel: "Ver malla sinterizada",
        },
        {
          title: "Mallas de alambre en cuña",
          text: "Paneles y cilindros electrosoldados de perfil en cuña para escurrido de alto rendimiento y cribado previo.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/industrial-wedge-wire-screen-panel-showi-bb2a47.png",
            alt: "Panel de alambre en cuña para separación industrial",
          },
          badge: "Familia de producto",
          linkLabel: "Ver alambre en cuña",
        },
      ],
    },
    cta: {
      id: "plastic-recycling-cta",
      eyebrow: "Consulta de filtración para reciclaje",
      heading: "Solicite paquetes de malla a medida para su línea de reciclaje",
      lead: "Indique el diámetro de su placa rompedora, modelo de cambiador, polímero y micras requeridas para recibir especificaciones y presupuesto.",
      secondaryLabel: "Contactar con ventas técnicas",
      secondaryTo: "/ContactPage",
      note: "Disponibles en AISI 304 y AISI 316L con respuesta técnica ágil.",
    },
  },
  de: {
    breadcrumbKey: "PlasticRecyclingFiltration",
    relatedTopic: "polymer",
    hero: {
      categoryBadge: "Polymerfiltrations-Anwendungen",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Schmelzefiltration im Kunststoffrecycling",
      lead: "Präzisionsgefertigte Drahtgewebepakete, Sinterfilterscheiben und Endlos-Siebbänder aus Edelstahl für hohe Schmutzfrachten bei der Aufbereitung von Post-Consumer- und Industrie-Rezyklaten.",
      secondaryCta: {
        label: "Übersicht Polymerfiltration",
        to: "/PolymerFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
        alt: "Edelstahl-Mehrlagen-Siebpaket und Filterscheiben für das Kunststoffrecycling",
      },
      cardNote: "Punktschweißpakete, alugefasste Ronden & Siebbänder",
      cardLink: {
        label: "Siebpakete ansehen",
        to: "/FilterComponentsPage",
      },
      specs: [
        { label: "Filterfeinheit", value: "10–200 µm" },
        { label: "Werkstoffgüten", value: "AISI 304 / 316L" },
        { label: "Ausführung", value: "Geschweißt / Gefasst / Band" },
        { label: "Einsatzbereich", value: "PCR / PIR Extrusion & Granulierung" },
      ],
    },
    overview: {
      eyebrow: "Prozessanforderungen",
      heading: "Kontinuierliche Schmelzereinigung bei starker Verunreinigung",
      paragraphs: [
        "Die Aufbereitung von Post-Consumer- und Post-Industrial-Kunststoffen stellt extreme Anforderungen an die Schmelzefiltration. Feste Fremdkörper, unaufgeschmolzene Gele, Papierfasern, Aluminiumflitter und Rußpartikel müssen kontinuierlich aus dem viskosen Schmelzestrom entfernt werden.",
        "Um Linienstillstände zu vermeiden, arbeiten moderne Recyclinglinien mit hydraulischen Schiebersiebwechslern, rotierenden Scheiben oder automatischen Siebbandwechslern. Die metallischen Filtermedien müssen eine hohe Schmutzaufnahmekapazität mit exakter Trennschärfe und mechanischer Stabilität gegen hohe Differenzdrücke verbinden.",
        "MutualFilter fertigt Mehrlagen-Siebpakete mit robusten Stützgeweben, kalibrierten Tressen-Filterschichten und diffusionsgebundenen Sinterplatten für kontrollierten Druckaufbau und maximale Standzeiten.",
      ],
      keyPoints: [
        "Hochfeste Quadratmaschen-Stützgitter schützen feine Filterschichten vor extremen Extrusionsdruckspitzen und verhindern Durchblasen.",
        "Kalibrierte glatte und umgekehrte Tressengewebe scheiden zersetzte Gele, Stippen und Metallpartikel zuverlässig ab.",
        "Lieferbar als punktgeschweißte Mehrlagenpakete, alugefasste Ronden und Endlos-Siebbänder für automatische Siebwechsler.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/polymer_extrusion_filter.png",
        alt: "Industrielles Schmelzefiltersieb und Lochplattenbaugruppe für Extruder",
      },
    },
    specTable: {
      heading: "Standard-Siebkonfigurationen für das Kunststoffrecycling",
      intro: "Typische Siebpaketaufbauten und Drahtgewebeschichten für Recyclingextruder und Siebwechselsysteme.",
      columns: [
        { key: "configuration", label: "Konfiguration", align: "left" },
        { key: "micronRating", label: "Nennfeinheit", align: "right" },
        { key: "layerStructure", label: "Typischer Lagenaufbau", align: "left" },
        { key: "changerType", label: "Siebwechsler-Typ", align: "left" },
      ],
      rows: [
        {
          id: "coarse-prep",
          configuration: "Grobfeststoffabtrennung / Vorfiltration",
          micronRating: "150–200 µm",
          layerStructure: "2- oder 3-lagig Quadratmasche (20 / 80 / 20 Mesh)",
          changerType: "Manuelle oder hydraulische Schiebersiebwechsler",
        },
        {
          id: "standard-pellet",
          configuration: "Standard-Rezyklatgranulierung",
          micronRating: "60–120 µm",
          layerStructure: "3- bis 5-lagig punktgeschweißt (20 / 60 / 150 / 60 / 20)",
          changerType: "Doppelkolben- und kontinuierliche Drehwechsler",
        },
        {
          id: "fine-purification",
          configuration: "Folien- und Plattenqualität PCR",
          micronRating: "25–50 µm",
          layerStructure: "Mehrlagig Glatte Tresse mit Hochdruckstützgewebe",
          changerType: "Rückspül- und Endlos-Siebbandwechsler",
        },
        {
          id: "micro-filtration",
          configuration: "Hochreine technische Kunststoffe",
          micronRating: "10–25 µm",
          layerStructure: "Sintergewebelaminat oder Köpertressenaufbau",
          changerType: "Hochdruck-Schmelzefiltrationsanlagen",
        },
      ],
      caption: "Alle Siebpakete und Bänder werden nach Maß in AISI 304 oder AISI 316L passend zu Ihrer Lochplatte gefertigt.",
    },
    features: {
      heading: "Konstruktive Vorteile für Recyclingbetriebe",
      intro: "Wesentliche Konstruktionsmerkmale für maximale Verfügbarkeit und gleichbleibende Schmelzequalität.",
      items: [
        {
          title: "Durchblassichere Stützkonstruktion",
          text: "Schwere Quadratmaschen verhindern das Einprägen feiner Filterschichten in die Bohrungen der Lochplatte.",
        },
        {
          title: "Präzise Porengeometrie",
          text: "Dichte Tressengewebe gewährleisten exakte Trenngrenzen und halten auch elastische Gelkörper sicher zurück.",
        },
        {
          title: "Dichtende Randeinfassungen",
          text: "Alu- oder Kupferfassungen verhindern Bypass-Leckagen unfiltrierter Schmelze am Lochplattenrand.",
        },
        {
          title: "Endlosbänder für automatischen Wechsel",
          text: "Umgekehrte Tressengewebe mit verstärkten Webkanten sichern den störungsfreien Bandvorschub.",
        },
      ],
    },
    related: {
      heading: "Verwandte Anwendungen und Produktfamilien",
      intro: "Entdecken Sie ergänzende Filtrationslösungen und unser Spektrum an Edelstahl-Drahtgeweben.",
      cards: [
        {
          title: "Polymerschmelzefiltration",
          text: "Kontinuierliche Schmelzefiltration für Neuware, Spinnvliese, Flachfolien und anspruchsvolle Compoundierung.",
          to: "/PolymerMeltFiltrationApplicationsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-15b8ef.png",
            alt: "Kontinuierlicher Siebwechsler für Polymerschmelzen",
          },
          badge: "Schwester-Anwendung",
          linkLabel: "Anwendung ansehen",
        },
        {
          title: "Übersicht Polymerfiltration",
          text: "Vollständige Übersicht an Filtermedien, Siebpaketen und Siebbändern für die Polymerverarbeitung.",
          to: "/PolymerFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-polymer-ext-65ec06.png",
            alt: "Filterpakete für die Polymerschmelzeextrusion",
          },
          badge: "Übergeordnete Anwendung",
          linkLabel: "Übersicht ansehen",
        },
        {
          title: "Filterkomponenten",
          text: "Gestanzte Ronden, punktgeschweißte Siebpakete, gefasste Filter und Siebbandrollen in 304 und 316L.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "Edelstahl-Filterscheiben und Siebpakete",
          },
          badge: "Produktfamilie",
          linkLabel: "Komponenten ansehen",
        },
        {
          title: "Edelstahldrahtgewebe",
          text: "Quadratmaschen und Tressengewebe nach ISO 9044 und ASTM E2016 für Filtration und Stützfunktionen.",
          to: "/WovenWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Glatte Tresse Drahtgewebe für Schmelzefiltration",
          },
          badge: "Produktfamilie",
          linkLabel: "Drahtgewebe ansehen",
        },
        {
          title: "Sintermetallgewebe",
          text: "Diffusionsgebundene Mehrlagen-Sinterplatten mit absoluter Fadenfixierung und mehrfacher Regenerierbarkeit.",
          to: "/SinteredWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Mehrlagige Sinterscheibe für Schmelzefiltration",
          },
          badge: "Produktfamilie",
          linkLabel: "Sintergewebe ansehen",
        },
        {
          title: "Spaltsiebe (Wedge Wire)",
          text: "Geschweißte Spaltrohre und Siebkörbe für die Grobabtrennung und Entwässerung im Kunststoffwaschprozess.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/industrial-wedge-wire-screen-panel-showi-bb2a47.png",
            alt: "Spaltsiebplatte für industrielle Fest-Flüssig-Trennung",
          },
          badge: "Produktfamilie",
          linkLabel: "Spaltsiebe ansehen",
        },
      ],
    },
    cta: {
      id: "plastic-recycling-cta",
      eyebrow: "Recycling-Filtrationsberatung",
      heading: "Maßgeschneiderte Siebpakete für Ihre Recyclinglinie anfragen",
      lead: "Nennen Sie Lochplattendurchmesser, Siebwechslermodell, Polymerart und Sollfeinheit für ein detailliertes Angebot.",
      secondaryLabel: "Technischen Vertrieb kontaktieren",
      secondaryTo: "/ContactPage",
      note: "Gefertigt in AISI 304 und AISI 316L nach Ihren spezifischen Einbaumaßen.",
    },
  },
  fr: {
    breadcrumbKey: "PlasticRecyclingFiltration",
    relatedTopic: "polymer",
    hero: {
      categoryBadge: "Applications de filtration des polymères",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Filtration pour le recyclage plastique",
      lead: "Packs multicouches en toile tissée inox, disques frittés et bandes continues conçus pour résister à des charges de contamination élevées lors du recyclage des polymères.",
      secondaryCta: {
        label: "Vue d'ensemble polymères",
        to: "/PolymerFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
        alt: "Ensemble de packs de toiles et disques filtrants pour le recyclage plastique",
      },
      cardNote: "Packs soudés par points, disques sertis et bandes continues",
      cardLink: {
        label: "Voir les packs de toiles",
        to: "/FilterComponentsPage",
      },
      specs: [
        { label: "Seuil de filtration", value: "10–200 µm" },
        { label: "Nuances d'alliage", value: "AISI 304 / 316L" },
        { label: "Structure", value: "Soudé / Sertis alu / Toile continue" },
        { label: "Application", value: "Extrusion et granulation PCR / PIR" },
      ],
    },
    overview: {
      eyebrow: "Exigences du procédé",
      heading: "Nettoyage continu des bains fondus à forte charge d'impuretés",
      paragraphs: [
        "Le retraitement des polymères plastiques post-consommation et industriels pose des défis majeurs. Particules solides, gels non fondus, fibres de papier, copeaux d'aluminium et particules carbonées doivent être séparés en continu avant la granulation ou l'outillage de profilés.",
        "Pour éviter les arrêts de ligne, les lignes d'extrusion de recyclage utilisent des changeurs de filtres hydrauliques à plaques, à disques rotatifs ou des toiles défilantes automatiques. Les médias filtrants métalliques doivent offrir une grande capacité de rétention, une coupure micrométrique nette et une résistance élevée aux fortes pressions différentielles.",
        "MutualFilter fournit des packs multicouches intégrant des toiles de soutien à mailles carrées, des toiles hollandaises calibrées et des disques frittés garantissant une montée en pression maîtrisée et une durée de service prolongée.",
      ],
      keyPoints: [
        "Grilles de support carrées haute résistance protégeant les couches fines contre les pics de pression et prévenant toute perforation.",
        "Tissages hollandais unis et inversés calibrés retenant gels dégradés, points noirs et résidus métalliques pour stabiliser la pression.",
        "Disponibles en packs multicouches soudés par points, disques sertis aluminium et bandes de toiles continues pour changeurs automatiques.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/polymer_extrusion_filter.png",
        alt: "Ensemble de grille de filtration continue de polymère fondu et plaque de rupture",
      },
    },
    specTable: {
      heading: "Configurations standard de toiles pour le recyclage plastique",
      intro: "Exemples de structures multicouches et de toiles métalliques adaptées aux extrudeuses de régénération plastique.",
      columns: [
        { key: "configuration", label: "Configuration", align: "left" },
        { key: "micronRating", label: "Seuil nominal", align: "right" },
        { key: "layerStructure", label: "Structure typique", align: "left" },
        { key: "changerType", label: "Type de changeur", align: "left" },
      ],
      rows: [
        {
          id: "coarse-prep",
          configuration: "Lavage lourd / Pré-filtration grossière",
          micronRating: "150–200 µm",
          layerStructure: "2 ou 3 couches mailles carrées (20 / 80 / 20 mesh)",
          changerType: "Changeurs à plaques coulissantes manuelles ou hydrauliques",
        },
        {
          id: "standard-pellet",
          configuration: "Granulation et régénération courante",
          micronRating: "60–120 µm",
          layerStructure: "3 à 5 couches soudées par points (20 / 60 / 150 / 60 / 20)",
          changerType: "Changeurs à double piston et rotatifs continus",
        },
        {
          id: "fine-purification",
          configuration: "Films et feuilles qualité PCR",
          micronRating: "25–50 µm",
          layerStructure: "Multicouche tissage hollandais uni avec renfort",
          changerType: "Changeurs à rétrolavage et bandes automatiques",
        },
        {
          id: "micro-filtration",
          configuration: "Plastiques techniques haute pureté",
          micronRating: "10–25 µm",
          layerStructure: "Stratifié fritté multicouche ou tissage hollandais sergé",
          changerType: "Systèmes de filtration continue haute pression",
        },
      ],
      caption: "Tous les packs et rouleaux sont fabriqués sur mesure en AISI 304 ou AISI 316L selon les dimensions de votre plaque de répartition.",
    },
    features: {
      heading: "Atouts techniques pour les ateliers de recyclage",
      intro: "Caractéristiques structurales majeures assurant productivité maximale et homogénéité des bains de matière recyclée.",
      items: [
        {
          title: "Soutien mécanique anti-extrusion",
          text: "Grilles porteuses épaisses empêchant l'enfoncement des toiles fines dans les orifices de la plaque de rupture.",
        },
        {
          title: "Géométrie des pores calibrée",
          text: "Tissages hollandais serrés offrant une coupure micrométrique nette sans passage de gels déformés.",
        },
        {
          title: "Bordures serties étanches",
          text: "Cadres sertis en aluminium ou cuivre assurant une étanchéité parfaite sans fuite périphérique de matière brute.",
        },
        {
          title: "Bandes continues pour changeurs automatiques",
          text: "Tissages hollandais inversés avec lisières renforcées pour un défilement continu sans déport.",
        },
      ],
    },
    related: {
      heading: "Applications associées et gammes de produits",
      intro: "Découvrez nos solutions complémentaires en filtration de polymères ainsi que nos gammes de toiles inox.",
      cards: [
        {
          title: "Filtration des polymères fondus",
          text: "Filtration continue pour résines vierges, filage de fibres, films et compoundage haute pureté.",
          to: "/PolymerMeltFiltrationApplicationsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-15b8ef.png",
            alt: "Changeur de filtre hydraulique continu pour polymères fondus",
          },
          badge: "Application sœur",
          linkLabel: "Voir l'application",
        },
        {
          title: "Vue d'ensemble filtration des polymères",
          text: "Gamme globale de toiles, packs multicouches et toiles continues pour le traitement des polymères.",
          to: "/PolymerFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-polymer-ext-65ec06.png",
            alt: "Packs de toiles pour extrusion de polymères",
          },
          badge: "Application parente",
          linkLabel: "Voir la vue d'ensemble",
        },
        {
          title: "Composants filtrants",
          text: "Disques découpés, packs soudés par points, assemblages sertis et bobines de toiles en 304 et 316L.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "Disques et packs filtrants en acier inoxydable",
          },
          badge: "Famille de produits",
          linkLabel: "Voir les composants",
        },
        {
          title: "Toile métallique tissée",
          text: "Toiles à mailles carrées et tissages hollandais selon ISO 9044 et ASTM E2016 pour filtration et support.",
          to: "/WovenWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Toile tissage hollandais uni pour filtration de polymère fondu",
          },
          badge: "Famille de produits",
          linkLabel: "Voir les toiles tissées",
        },
        {
          title: "Toile métallique frittée",
          text: "Stratifiés multicouches frittés par diffusion avec fixation absolue des fils et nettoyage répété.",
          to: "/SinteredWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Disque laminé fritté pour filtration de polymères",
          },
          badge: "Famille de produits",
          linkLabel: "Voir les toiles frittées",
        },
        {
          title: "Grilles à fil profilé (Wedge Wire)",
          text: "Panneaux et paniers à fentes continues pour l'égouttage et le criblage amont des broyats plastiques.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/industrial-wedge-wire-screen-panel-showi-bb2a47.png",
            alt: "Panneau de grille à fil profilé pour séparation solide-liquide",
          },
          badge: "Famille de produits",
          linkLabel: "Voir le fil profilé",
        },
      ],
    },
    cta: {
      id: "plastic-recycling-cta",
      eyebrow: "Conseil en filtration de recyclage",
      heading: "Demandez vos packs de toiles sur mesure pour votre ligne de recyclage",
      lead: "Transmettez le diamètre de votre plaque de répartition, le modèle de changeur, le polymère et le micronage requis pour obtenir une offre technique.",
      secondaryLabel: "Contacter le service commercial",
      secondaryTo: "/ContactPage",
      note: "Fabrication en AISI 304 et AISI 316L selon vos cotes machine précises.",
    },
  },
  pt: {
    breadcrumbKey: "PlasticRecyclingFiltration",
    relatedTopic: "polymer",
    hero: {
      categoryBadge: "Aplicações de filtração de polímeros",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Filtração para reciclagem de plásticos",
      lead: "Conjuntos de telas tecidas em aço inoxidável, discos sinterizados e esteiras contínuas projetados para altas cargas de contaminantes na extrusão de plásticos reciclados.",
      secondaryCta: {
        label: "Visão geral de polímeros",
        to: "/PolymerFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
        alt: "Conjunto de tela filtrante multicamadas e discos para reciclagem de plástico",
      },
      cardNote: "Pacotes soldados por ponto, discos emoldurados e fitas contínuas",
      cardLink: {
        label: "Ver pacotes de telas",
        to: "/FilterComponentsPage",
      },
      specs: [
        { label: "Grau de retenção", value: "10–200 µm" },
        { label: "Ligas metálicas", value: "AISI 304 / 316L" },
        { label: "Construção", value: "Soldado / Borda de alumínio / Fita" },
        { label: "Aplicação", value: "Extrusão e granulação PCR / PIR" },
      ],
    },
    overview: {
      eyebrow: "Requisitos de processo",
      heading: "Purificação contínua de polímero fundido com alta contaminação",
      paragraphs: [
        "O reprocessamento de plásticos pós-consumo (PCR) e pós-industriais (PIR) impõe desafios rigorosos à filtração. Partículas sólidas, géis não fundidos, resíduos de papel, fragmentos de alumínio e pontos de carbono devem ser retidos continuamente do fluxo fundido antes da granulação.",
        "Para evitar paradas de linha, sistemas modernos de reciclagem utilizam trocadores de tela de placa deslizante, discos rotativos ou esteiras automáticas contínuas. Os meios filtrantes metálicos precisam combinar alta capacidade de retenção de sólidos, corte micrométrico preciso e rigidez mecânica contra altas pressões diferenciais.",
        "A MutualFilter fornece pacotes multicamadas com telas de suporte de malha quadrada, camadas de controle em trama holandesa e discos sinterizados desenvolvidos para elevação controlada de pressão e maior durabilidade operacional.",
      ],
      keyPoints: [
        "Telas de suporte de malha quadrada de alta resistência protegem as camadas finas contra picos severos de pressão de extrusão.",
        "Tramas holandesas lisas e reversas calibradas retêm géis degradados, pontos pretos e partículas metálicas finas.",
        "Disponíveis como pacotes multicamadas soldados por ponto, discos com borda de alumínio e fitas contínuas para trocadores automáticos.",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/polymer_extrusion_filter.png",
        alt: "Conjunto de tela de filtração contínua de polímero fundido e placa de suporte",
      },
    },
    specTable: {
      heading: "Configurações padrão de telas para reciclagem de plástico",
      intro: "Estruturas representativas de conjuntos de telas e camadas metálicas utilizadas em extrusoras de reciclagem.",
      columns: [
        { key: "configuration", label: "Configuração", align: "left" },
        { key: "micronRating", label: "Retenção nominal", align: "right" },
        { key: "layerStructure", label: "Estrutura típica de camadas", align: "left" },
        { key: "changerType", label: "Compatibilidade de trocador", align: "left" },
      ],
      rows: [
        {
          id: "coarse-prep",
          configuration: "Lavagem pesada / Separação grossa",
          micronRating: "150–200 µm",
          layerStructure: "2 ou 3 camadas de malha quadrada (20 / 80 / 20 mesh)",
          changerType: "Trocadores de placa deslizante manuais ou hidráulicos",
        },
        {
          id: "standard-pellet",
          configuration: "Granulação e reprocessamento padrão",
          micronRating: "60–120 µm",
          layerStructure: "3 a 5 camadas soldadas por ponto (20 / 60 / 150 / 60 / 20)",
          changerType: "Trocadores de duplo pistão e contínuos rotativos",
        },
        {
          id: "fine-purification",
          configuration: "Chapas e filmes grau PCR",
          micronRating: "25–50 µm",
          layerStructure: "Multicamadas em trama holandesa lisa com reforço",
          changerType: "Trocadores com retrolavagem e esteiras contínuas",
        },
        {
          id: "micro-filtration",
          configuration: "Resinas técnicas de alta pureza",
          micronRating: "10–25 µm",
          layerStructure: "Laminado sinterizado multicamadas ou trama sarja",
          changerType: "Sistemas contínuos de alta pressão",
        },
      ],
      caption: "Todas as telas e esteiras são fabricadas sob medida em AISI 304 ou AISI 316L conforme as dimensões da placa de suporte.",
    },
    features: {
      heading: "Vantagens de engenharia para operações de reciclagem",
      intro: "Características estruturais que asseguram máxima disponibilidade e estabilidade na qualidade do fundido.",
      items: [
        {
          title: "Suporte anticolapso de alta resistência",
          text: "Telas de malha quadrada espessa evitam a deformação das camadas finas nos furos da placa de suporte.",
        },
        {
          title: "Geometria de poros calibrada",
          text: "Tramas holandesas precisas garantem corte micrométrico definido e impedem a passagem de géis esticados.",
        },
        {
          title: "Bordas emolduradas com vedação",
          text: "Aros de alumínio ou cobre eliminam desfiamentos e impedem o desvio periférico de material sem filtrar.",
        },
        {
          title: "Fitas contínuas para avanço automático",
          text: "Tramas holandesas reversas com bordas reforçadas proporcionam tração estável em trocadores automáticos.",
        },
      ],
    },
    related: {
      heading: "Aplicações relacionadas e famílias de produtos",
      intro: "Conheça outras soluções em filtração de polímeros e nossas linhas de telas metálicas em aço inoxidável.",
      cards: [
        {
          title: "Filtração de polímero fundido",
          text: "Filtração contínua para resina virgem, fiação de fibras, filmes e compostos técnicos de alta pureza.",
          to: "/PolymerMeltFiltrationApplicationsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-15b8ef.png",
            alt: "Conjunto hidráulico de trocador de telas para polímero fundido",
          },
          badge: "Aplicação irmã",
          linkLabel: "Ver aplicação",
        },
        {
          title: "Visão geral de filtração de polímeros",
          text: "Visão abrangente de meios filtrantes, telas combinadas e esteiras contínuas para processamento de polímeros.",
          to: "/PolymerFiltrationPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-polymer-ext-65ec06.png",
            alt: "Telas filtrantes para extrusão de polímeros",
          },
          badge: "Aplicação pai",
          linkLabel: "Ver visão geral",
        },
        {
          title: "Componentes filtrantes",
          text: "Discos estampados sob medida, telas soldadas por ponto, conjuntos emoldurados e fitas em 304 e 316L.",
          to: "/FilterComponentsPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-round-stain-593637.png",
            alt: "Discos filtrantes em aço inoxidável para extrusão",
          },
          badge: "Família de produtos",
          linkLabel: "Ver componentes",
        },
        {
          title: "Telas metálicas tecidas",
          text: "Telas de malha quadrada e tramas holandesas conforme ISO 9044 e ASTM E2016 para filtração e suporte.",
          to: "/WovenWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
            alt: "Tela de arame inox de trama holandesa lisa para filtração de polímeros",
          },
          badge: "Família de produtos",
          linkLabel: "Ver telas tecidas",
        },
        {
          title: "Telas sinterizadas",
          text: "Laminados sinterizados por difusão que oferecem rigidez integral, sem desprendimento de fios e laváveis.",
          to: "/SinteredWireMeshPage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Disco laminado sinterizado para filtração de fundido",
          },
          badge: "Família de produtos",
          linkLabel: "Ver telas sinterizadas",
        },
        {
          title: "Telas de arame em cunha (Wedge Wire)",
          text: "Painéis e cilindros soldados de perfil cunha para desaguamento de alta capacidade e pré-filtragem.",
          to: "/WedgeWireScreenPage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/industrial-wedge-wire-screen-panel-showi-bb2a47.png",
            alt: "Painel de arame em cunha para separação industrial",
          },
          badge: "Família de produtos",
          linkLabel: "Ver telas cunha",
        },
      ],
    },
    cta: {
      id: "plastic-recycling-cta",
      eyebrow: "Consulta de filtração para reciclagem",
      heading: "Solicite pacotes de telas sob medida para a sua linha de reciclagem",
      lead: "Informe o diâmetro da placa de suporte, modelo do trocador, tipo de resina e mícron desejado para receber proposta técnica.",
      secondaryLabel: "Falar com vendas técnicas",
      secondaryTo: "/ContactPage",
      note: "Disponíveis em AISI 304 e AISI 316L com suporte técnico ágil.",
    },
  },
};
